/**
 * Nag (core nag.ts; docs/BACKLOG.md B19): push the person who can do it now.
 *  - NagButton: "Nag Sam" / "Nag someone" on an overdue check, an open problem or an open report (managers and owners).
 *  - NagSheet: the suggested person first, the text already written, one big "Text Sam" that opens Hit Em Up (the only
 *    texting channel, owner decision), and the ladder ("Nagged Sam 10 min ago · next: Ana at 2:25 PM"). Until Hit Em Up
 *    is connected (HITEMUP_COMPOSE_URL), the big button is "Copy the message". Someone who can't work today shows "Not
 *    available today", never why. Never sms:.
 *  - HomeNag: an adult nags a household member about a chore (same words; the link opens the chore's steps).
 * The nag is recorded as Hit Em Up opens or the text is copied (a real link, so the phone never blocks it).
 */
import { useEffect, useState } from "react";
import { HITEMUP_NOT_CONNECTED, choreNagItem, firstName, hitemupComposeUrl, nagLink, nagMessage, type NagItem, type NagView } from "@logwave/core";
import type { HouseholdMember } from "../api.ts";
import { config } from "../env.ts";
import { saveErrorText } from "../lib/failures.ts";
import { useApi, useSession } from "../session.tsx";
import { AnchorButton, Button, ErrorBox, Notice, Pill, Sheet, Skeleton } from "./ui.tsx";

/** Copies the text; true when the clipboard took it. */
async function copyText(body: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(body); return true; } catch { return false; }
}

/** The one big action: open Hit Em Up with the text, or (not connected yet) copy it. `onSend` records the nag. */
function SendButton({ href, body, who, onSend, testId, problem }: { href?: string; body: string; who: string; onSend: () => void; testId?: string; problem?: string }) {
  const [copied, setCopied] = useState<boolean>();
  if (href) return <AnchorButton big block href={href} icon="text" testId={testId} onClick={onSend}>{`Text ${who}`}</AnchorButton>;
  return (
    <>
      <Button big block icon="text" data-testid={testId} onClick={() => { void copyText(body).then((ok) => { setCopied(ok); onSend(); }); }}>{copied ? "Copied ✓" : "Copy the message"}</Button>
      <p className="muted" data-testid="nag-not-connected">{copied === false ? "Couldn't copy here: select the text above and copy it. " : ""}{problem ?? HITEMUP_NOT_CONNECTED}</p>
    </>
  );
}

/** "Nag Sam" when the server suggests someone, else "Nag someone". Loads the sheet only when opened. */
export function NagButton({ item, small = true, suggested }: { item: Pick<NagItem, "ref" | "title" | "urgency">; small?: boolean; suggested?: string }) {
  const s = useSession();
  const [open, setOpen] = useState(false);
  if (!s.can("manager")) return null;
  return (
    <>
      <Button small={small} variant={item.urgency === "urgent" ? "danger" : "secondary"} icon="text" onClick={() => setOpen(true)} data-testid="nag-open">
        {suggested ? `Nag ${firstName(suggested)}` : "Nag someone"}
      </Button>
      {open && <NagSheet item={item} onClose={() => setOpen(false)} />}
    </>
  );
}

export function NagSheet({ item, onClose }: { item: Pick<NagItem, "ref" | "title" | "urgency">; onClose: () => void }) {
  const api = useApi();
  const s = useSession();
  const [view, setView] = useState<NagView>();
  const [err, setErr] = useState<string>();
  const [pick, setPick] = useState<string>();
  const [sent, setSent] = useState<string>();
  const load = () => api.nags(s.org.id, { locationId: s.location.id, itemRef: item.ref }).then((v) => { setView(v); setPick((p) => p ?? v.suggested?.employeeId ?? v.texts[0]?.employeeId); }, (e) => setErr(saveErrorText(e)));
  useEffect(() => { void load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [item.ref]);

  const text = view?.texts.find((t) => t.employeeId === pick) ?? view?.texts[0];
  const who = text ? firstName(text.name) : "";
  const held = view?.available.find((a) => a.employeeId === text?.employeeId)?.heldUntil;
  const heldAt = held ? new Date(held).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: s.location.timezone }) : undefined;

  // Recorded as Hit Em Up opens (or the text is copied): the notice goes on their list, the ladder moves.
  function record(personIds: string[]) {
    api.nag(s.org.id, { locationId: s.location.id, itemRef: item.ref, personIds }).then((r) => {
      setView(r);
      setSent(`Nagged ${r.sent.map((t) => firstName(t.name)).join(" and ")}. It's on their list too. It's done when the log is saved, not when they reply.`);
    }, (e) => setErr(saveErrorText(e)));
  }

  return (
    <Sheet kicker={item.urgency === "urgent" ? "Urgent" : "Nag"} title={text ? `Nag ${who}` : "Nag someone"} onClose={onClose}>
      <div className="stack" data-testid="nag-sheet">
        <p className="lede">{item.title}</p>
        {err && <ErrorBox error={err} />}
        {!view && !err && <Skeleton rows={2} label="Finding who can do it" />}
        {view?.done && <Notice tone="go" title="Done">{view.ladder.line} The log is saved, so nobody needs a nag.</Notice>}
        {view && !view.done && (
          <>
            {view.texts.length > 1 && (
              <div className="chips" role="radiogroup" aria-label="Who">
                {view.texts.map((t) => (
                  <button key={t.employeeId} type="button" role="radio" aria-checked={t.employeeId === text?.employeeId} className={`chip${t.employeeId === text?.employeeId ? " chip-on" : ""}`} onClick={() => setPick(t.employeeId)}>
                    {firstName(t.name)}{t.employeeId === view.suggested?.employeeId ? " (suggested)" : ""}
                  </button>
                ))}
              </div>
            )}
            {view.unavailable.length > 0 && <p className="muted" data-testid="nag-unavailable">{view.unavailable.map((u) => `${firstName(u.name)}: ${u.line}`).join(" · ")}</p>}
            {!text && <Notice tone="warn" title="Nobody on shift can do this now">{view.ladder.line}</Notice>}
            {text && (
              <>
                <blockquote className="nag-text" data-testid="nag-text">{text.body}</blockquote>
                {view.quiet && item.urgency !== "urgent" && <Notice tone="warn">{view.quiet}</Notice>}
                {heldAt ? <Notice tone="info">{`${who} was just nagged. You can nag again at ${heldAt}.`}</Notice>
                  : !(view.quiet && item.urgency !== "urgent") && (
                    <SendButton href={text.href} body={text.body} who={who} problem={text.problem} testId="nag-text-one" onSend={() => record([text.employeeId])} />
                  )}
                {text.note && <Notice tone="warn"><span data-testid="nag-skipped">{text.note}</span></Notice>}
                {!text.hasPhone && text.href && <p className="muted">No phone number for {who} yet: Hit Em Up opens with the text, and you pick them.</p>}
                {view.group?.href && !heldAt && !view.quiet && <AnchorButton block variant="secondary" href={view.group.href} icon="people" onClick={() => record(view.available.filter((a) => a.hasPhone && !a.heldUntil).map((a) => a.employeeId))}>Text everyone on shift (one group)</AnchorButton>}
                {view.group && (view.group.note || view.group.problem) && !heldAt && !view.quiet && <p className="muted" data-testid="nag-group-note">{[view.group.problem, view.group.note].filter(Boolean).join(" ")}</p>}
              </>
            )}
            {sent && <Notice tone="go" title="Nag recorded">{sent}</Notice>}
            <p className="muted" data-testid="nag-ladder"><Pill tone="info" icon="clock">Next step</Pill> {view.ladder.line}</p>
          </>
        )}
      </div>
    </Sheet>
  );
}

/** Home: Nag a household member about a chore. Hidden when nobody else is in the home yet (household invite). */
export function HomeNag({ chore, members, myName }: { chore: { id: string; title: string }; members: HouseholdMember[]; myName?: string }) {
  const [open, setOpen] = useState(false);
  const [pick, setPick] = useState(members[0]?.userId);
  if (!members.length) return null;
  const item = choreNagItem(chore);
  const person = members.find((m) => m.userId === pick) ?? members[0]!;
  const body = nagMessage({ item, person, from: myName, urgency: "normal", link: nagLink(item.ref) });
  const who = firstName(person.name);
  return (
    <>
      <Button small variant="secondary" icon="text" onClick={() => setOpen(true)} data-testid="home-nag-open">{members.length === 1 ? `Nag ${firstName(members[0]!.name)}` : "Nag someone"}</Button>
      {open && (
        <Sheet kicker="Nag" title={`Nag ${who}`} onClose={() => setOpen(false)}>
          <div className="stack" data-testid="home-nag-sheet">
            <p className="lede">{chore.title}</p>
            {members.length > 1 && (
              <div className="chips" role="radiogroup" aria-label="Who">
                {members.map((m) => <button key={m.userId} type="button" role="radio" aria-checked={m.userId === person.userId} className={`chip${m.userId === person.userId ? " chip-on" : ""}`} onClick={() => setPick(m.userId)}>{firstName(m.name)}</button>)}
              </div>
            )}
            <blockquote className="nag-text" data-testid="home-nag-text">{body}</blockquote>
            <SendButton href={hitemupComposeUrl({ composeUrl: config.hitemupComposeUrl || undefined, phones: [], body, ref: item.ref })} body={body} who={who} testId="home-nag-send" onSend={() => undefined} />
            <p className="muted">The link opens the steps. It's done when {who} marks it done.</p>
          </div>
        </Sheet>
      )}
    </>
  );
}
